import type { DomainDataProfile } from '../../core/data-profiles';
import { projectNetworkScenario } from './scenarios/networkScenarioProjector';

/**
 * Network domain semantic node + edge types — the CANVAS / Compass-bridge
 * vocabulary. Provider-agnostic: AWS, Azure, GCP shapes all map to the same
 * semantic types, with `props.hostingProvider` carrying the provider.
 *
 * Note on the dual vocabulary: there is a SEPARATE, narrower
 * `NetworkNodeType` / `NetworkEdgeType` in `src/lib/graph/types.ts` used by
 * the hydrator (`interop/NetworkHydrator.ts`) for cross-domain federation
 * interop. The hydrator translates the rich types here into those abstract
 * buckets so a single folder graph can hold network + process + GRC nodes.
 * Keep them aligned conceptually but don't try to merge them — they serve
 * different layers (canvas/Compass vs. federation join engine).
 *
 * Inheritance hierarchy for concrete types is declared in
 * `src/domains/network/ontology.ts` and registered via
 * `registerNetworkOntology()`. Use `isA()` from `src/lib/graph/ontology.ts`
 * to query at any abstraction level.
 */
export type NetworkNodeType =
  // Compute
  | 'server'
  | 'function_service'
  | 'container_instance'
  | 'container_orchestrator'
  | 'kubernetes_cluster'
  | 'application'
  | 'managed_compute'
  // Storage abstractions
  | 'object_storage'
  | 'file_storage'
  | 'block_storage'
  | 's3_bucket'
  | 'ebs_volume'
  | 'efs_volume'
  // Database abstractions
  | 'database'
  | 'relational_database'
  | 'document_database'
  | 'key_value_store'
  | 'graph_database'
  | 'wide_column_store'
  | 'time_series_database'
  | 'vector_database'
  | 'search_index'
  | 'data_warehouse'
  // Database products
  | 'postgres' | 'mysql' | 'mariadb' | 'oracle' | 'sqlserver'
  | 'aurora_postgres' | 'aurora_mysql'
  | 'mongodb' | 'dynamodb' | 'cosmosdb' | 'firestore'
  | 'redis' | 'memcached'
  | 'cassandra' | 'bigtable'
  | 'neo4j' | 'neptune'
  | 'redshift' | 'bigquery' | 'snowflake'
  | 'elasticsearch' | 'opensearch'
  | 'timestream' | 'influxdb'
  // Network plane
  | 'network_boundary'
  | 'subnet'
  | 'load_balancer'
  | 'api_gateway'
  | 'cdn'
  | 'dns_zone'
  | 'network_edge'
  // Identity
  | 'service_identity'
  | 'user_identity'
  | 'identity_group'
  | 'identity_provider'
  | 'cognito_user_pool'
  // Security
  | 'network_acl'
  | 'access_policy'
  | 'encryption_key'
  | 'certificate'
  | 'secret_store'
  | 'web_app_firewall'
  | 'threat_detector'
  // Messaging
  | 'message_queue'
  | 'message_topic'
  | 'event_stream'
  | 'event_bus'
  // Observability
  | 'log_store'
  | 'metric_store'
  | 'audit_log'
  | 'trace_store'
  // Geographic / organizational
  | 'geographic_region'
  | 'availability_zone'
  | 'account';

export type NetworkEdgeType =
  | 'contained_in'
  | 'allows_traffic_to'
  | 'load_balances_to'
  | 'reads_from'
  | 'writes_to'
  | 'caches'
  | 'publishes_to'
  | 'subscribes_to'
  | 'assumes_identity'
  | 'authenticates_via'
  | 'encrypted_by'
  | 'reads_secret_from'
  | 'logs_to'
  | 'monitored_by'
  | 'depends_on'
  | 'replicates_to'
  | 'backs_up_to'
  | 'runs_on';

export interface NetworkUniversalNodeProps {
  /** Unique resource identifier (ARN, GCP self-link, Azure resource ID) */
  resourceId: string;
  name: string;

  /** Provider hosting this resource: 'aws' | 'azure' | 'gcp' | 'on-prem' | 'other' */
  hostingProvider?: string;
  /** Runtime substrate when different from host provider, e.g. 'kubernetes'. */
  runtimeProvider?: string;
  /** Visual/source dialect pack, e.g. 'aws', 'azure', 'gcp', 'kubernetes'. */
  dialectId?: string;
  /** Provider-native resource type, e.g. Microsoft.Network/virtualNetworks. */
  providerLocalType?: string;
  /** Optional managed-service product name (e.g. 'rds', 'aurora', 'cloud_sql'). null = self-hosted. */
  managedService?: string;

  region?: string;
  az?: string;
  accountId?: string;
  tags?: Record<string, string>;

  // Common semantic facets queried by Compass / AI tools
  encryptedAtRest?: boolean;
  publicExposure?: 'none' | 'restricted' | 'open';
  containsPII?: boolean;
  containsPHI?: boolean;
  containsPCI?: boolean;
  monthlySpend?: number;

  [key: string]: unknown;
}

export interface NetworkUniversalEdgeProps {
  /** Original domain-specific edge kind preserved for round-trip fidelity */
  infraEdgeKind?: string;
  port?: number;
  protocol?: string;
  via?: string;
  [key: string]: unknown;
}

export const NetworkProfile: DomainDataProfile<
  NetworkNodeType,
  NetworkEdgeType,
  NetworkUniversalNodeProps,
  NetworkUniversalEdgeProps
> = {
  domainId: 'network',
  profileVersion: '1.0',
  diagramTypes: ['network'],
  nodeTypes: [
    'server', 'function_service', 'container_instance', 'container_orchestrator',
    'kubernetes_cluster', 'application', 'managed_compute',
    'object_storage', 'file_storage', 'block_storage', 's3_bucket', 'ebs_volume', 'efs_volume',
    'database', 'relational_database', 'document_database', 'key_value_store',
    'graph_database', 'wide_column_store', 'time_series_database', 'vector_database',
    'search_index', 'data_warehouse',
    'postgres', 'mysql', 'mariadb', 'oracle', 'sqlserver',
    'aurora_postgres', 'aurora_mysql',
    'mongodb', 'dynamodb', 'cosmosdb', 'firestore',
    'redis', 'memcached',
    'cassandra', 'bigtable', 'neo4j', 'neptune',
    'redshift', 'bigquery', 'snowflake',
    'elasticsearch', 'opensearch', 'timestream', 'influxdb',
    'network_boundary', 'subnet', 'load_balancer', 'api_gateway', 'cdn', 'dns_zone', 'network_edge',
    'service_identity', 'user_identity', 'identity_group', 'identity_provider', 'cognito_user_pool',
    'network_acl', 'access_policy', 'encryption_key', 'certificate', 'secret_store',
    'web_app_firewall', 'threat_detector',
    'message_queue', 'message_topic', 'event_stream', 'event_bus',
    'log_store', 'metric_store', 'audit_log', 'trace_store',
    'geographic_region', 'availability_zone', 'account',
  ],
  edgeTypes: [
    'contained_in', 'allows_traffic_to', 'load_balances_to',
    'reads_from', 'writes_to', 'caches',
    'publishes_to', 'subscribes_to',
    'assumes_identity', 'authenticates_via',
    'encrypted_by', 'reads_secret_from',
    'logs_to', 'monitored_by',
    'depends_on', 'replicates_to', 'backs_up_to', 'runs_on',
  ],

  vocabulary: {
    recordSingular: 'Resource',
    recordPlural: 'Resources',
    repositoryLabel: 'Infrastructure Inventory',
    hierarchyLabel: 'Contains',
    importAction: 'Import Cloud Scan',
    keyColumnHint: 'Resource ID / ARN',
    commitLabel: 'Export Changes',
    pullLabel: 'Re-scan',
    diagramLabel: 'infrastructure diagram',
  },

  fieldAliases: {
    resourceId: ['arn', 'resource_id', 'id', 'self_link'],
    name: ['resource_name', 'device_name', 'hostname', 'display_name'],
    hostingProvider: ['cloud', 'provider', 'platform'],
    runtimeProvider: ['runtime', 'runtime_provider', 'substrate'],
    dialectId: ['dialect', 'provider_dialect', 'shape_library'],
    providerLocalType: ['provider_type', 'native_type', 'asset_type', 'resource_type'],
    region: ['aws_region', 'gcp_region', 'azure_region', 'location'],
    az: ['availability_zone', 'zone'],
    accountId: ['account', 'aws_account', 'project_id', 'subscription_id'],
    tags: ['labels', 'metadata'],
  },

  requiredProps: ['resourceId', 'name'],
  recommendedProps: ['hostingProvider', 'region'],

  primaryShapeType: 'aws_ec2',

  propsToAttrsMapping: {
    resourceId: 'resourceId',
    resourceType: 'resourceType',
    sourceResourceType: 'sourceResourceType',
    name: 'name',
    hostingProvider: 'hostingProvider',
    runtimeProvider: 'runtimeProvider',
    dialectId: 'dialectId',
    providerLocalType: 'providerLocalType',
    managedService: 'managedService',
    service: 'service',
    region: 'region',
    az: 'az',
    accountId: 'accountId',
    encryptedAtRest: 'encryptedAtRest',
    publicExposure: 'publicExposure',
    backupRetentionDays: 'backupRetentionDays',
    hasPublicIngress: 'hasPublicIngress',
    hasPublicSsh: 'hasPublicSsh',
    publicIngressRuleCount: 'publicIngressRuleCount',
    monthlySpend: 'monthlySpend',
  },

  relationshipPackId: 'network-relationships',

  hierarchyConfig: {
    type: 'multi-parent',
    // True structural containment only: account → region → VPC → subnet.
    // Security groups are intentionally NOT in this list — SGs govern access,
    // they don't contain resources, and modeling them as `contains` parents
    // would inject phantom containment edges into the federated graph.
    // SG membership is expressed via the `allows_traffic_to` edge from the
    // relationship pack (`network_access` category) instead.
    parentProps: ['account', 'region', 'vpc', 'subnet'],
    edgeSubtypes: {
      account: 'contains',
      region: 'contains',
      vpc: 'contains',
      subnet: 'contains',
    },
  },

  scenarioConfig: {
    supportedOperations: ['add', 'remove', 'update'],
    trackableProps: [
      'name',
      'resourceId',
      'region',
      'publicExposure',
      'encrypted',
      'monthlySpend',
      'cidr',
      'instanceType',
      'engine',
      'tags',
    ],
    projector: (baseNodes, baseEdges, delta) =>
      projectNetworkScenario(
        baseNodes as any,
        baseEdges as any,
        delta as Parameters<typeof projectNetworkScenario>[2],
      ),
  },

  onboardingModes: ['blank', 'demo', 'template'],
};
